import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import i18n from '@src/core/i18n';
import { useVerifyMfaMutation } from '@src/features/auth/redux';
import { ACCESS_TOKEN_KEY } from '@src/core/config';
import { routesConfig } from '@src/core/routes';
import { trackError, toTrackedError } from '@src/core/sentry';
import { getMfaErrorBody, getMfaErrorCode, MFA_ERROR_CODE } from '@src/features/auth/utils/mfa-error';

export interface IVerifyTotpForm {
  code: string;
}

// Drives the second login step. The mfaToken arrives via router state (set by useSignInForm)
// and is kept only in memory — it is never written to localStorage.
export const useVerifyTotpForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const mfaToken = (location.state as { mfaToken?: string } | null)?.mfaToken;

  const [verifyMfa, { isLoading }] = useVerifyMfaMutation();
  const form = useForm<IVerifyTotpForm>({ defaultValues: { code: '' } });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lockSeconds, setLockSeconds] = useState(0);
  // The /authenticate/mfa endpoint accepts either a 6-digit TOTP code or a backup code in the
  // same `code` field. This toggle only swaps the on-screen guidance/labels so a user without
  // their phone knows they can paste a backup code instead.
  const [useBackupCode, setUseBackupCode] = useState(false);
  const lockTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // No challenge token (deep link / refresh) → restart from sign in.
  useEffect(() => {
    if (!mfaToken) {
      navigate(routesConfig.signIn, {
        replace: true,
        // Pass a translation key; sign-in-page localizes it at the point of display.
        state: { mfaMessageKey: 'mfa.login.sessionExpired' },
      });
    }
  }, [mfaToken, navigate]);

  useEffect(() => {
    return () => {
      if (lockTimer.current) clearInterval(lockTimer.current);
    };
  }, []);

  const startLockCountdown = (seconds: number) => {
    setLockSeconds(seconds);
    if (lockTimer.current) clearInterval(lockTimer.current);
    lockTimer.current = setInterval(() => {
      setLockSeconds((prev) => {
        if (prev <= 1) {
          if (lockTimer.current) clearInterval(lockTimer.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const onSubmit = async (data: IVerifyTotpForm) => {
    if (!mfaToken || lockSeconds > 0) return;
    setErrorMessage(null);

    try {
      const response = await verifyMfa({ mfaToken, code: data.code.trim() }).unwrap();
      localStorage.setItem(ACCESS_TOKEN_KEY, response.id_token);
      navigate(routesConfig.app, { replace: true });
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      const code = getMfaErrorCode(err);

      if (status === 429 || code === MFA_ERROR_CODE.LOCKED) {
        const retryAfter = getMfaErrorBody(err).retryAfterSeconds ?? 30;
        startLockCountdown(retryAfter);
        setErrorMessage(null);
        return;
      }

      // Expired / invalid challenge token — the 5-minute window is gone; send the user back
      // to sign in with an explanatory message.
      if (code === MFA_ERROR_CODE.TOKEN_INVALID) {
        navigate(routesConfig.signIn, {
          replace: true,
          // Pass a translation key; sign-in-page localizes it at the point of display.
          state: { mfaMessageKey: 'mfa.login.sessionExpired' },
        });
        return;
      }

      // Wrong code (401 MFA_CODE_INVALID) — keep the user here and let them retry.
      if (status === 401 || code === MFA_ERROR_CODE.CODE_INVALID) {
        setErrorMessage(i18n.t('mfa.errors.MFA_CODE_INVALID'));
        form.setValue('code', '');
        return;
      }

      setErrorMessage(i18n.t('mfa.errors.verifyFailed'));
      trackError(toTrackedError(err), { code: 'FE_0022', extra: { flow: 'verifyMfa' } });
    }
  };

  return {
    ...form,
    onSubmit: form.handleSubmit(onSubmit),
    isLoading,
    errorMessage,
    lockSeconds,
    hasToken: Boolean(mfaToken),
    useBackupCode,
    toggleBackupCode: () => {
      setErrorMessage(null);
      form.setValue('code', '');
      setUseBackupCode((prev) => !prev);
    },
  };
};
